import React, { useContext, Fragment, useState, useRef, useMemo, useEffect } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { lighten } from 'polished';
import Checkbox from 'shared/components/Checkbox'

import Box from 'shared/components/Box'
import { ParticipantRowItem } from 'pages/Participants/model/participant'
import upperFirst from 'lodash/upperFirst'

import screeningFormValidator, {
  ScreeningFormType,
  ScreeningAnswerType,
  ScreeningQuestionType,
  ScreeningParticipantType,
  getDummyQuestions,
  getEmptyScreeningForm
} from 'shared/components/Screening/models/screening'

interface CollapsableFormContainerProps {
  participant?: ScreeningParticipantType
  children: React.ReactNode
  isPreview?: boolean
  onToggle: (show: boolean) => void
  onSelect: (val: boolean) => void
}

interface CollapsableContentProps {
  children: React.ReactNode
}

function CollapsableFormContainer(props: CollapsableFormContainerProps) {
  const theme = useContext(ThemeContext)
  const [isOpen, setOpen] = useState(false)
  const contentReference = useRef<HTMLDivElement>(null)
  const { isPreview = false, participant, onToggle, onSelect } = props

  const isFilled = useMemo(() => {
    return participant?.isSelected && participant?.question_count === participant?.answers?.length
  }, [participant])

  useEffect(() => {
    if (isOpen && contentReference?.current) {
      contentReference.current.style.maxHeight = `${contentReference?.current?.scrollHeight}px`
    }
  }, [contentReference?.current])

  if (isPreview && !participant?.isSelected)
    return <Fragment />

  if (isPreview)
    return (
      <CollapsablePreviewContainer>
        <div className="collapse-name">{`${upperFirst(participant?.name)}’s Screening Form`}</div>
        {props.children}
      </CollapsablePreviewContainer>
    )

  return (
    <CollapsableContainer $isOpen={isOpen}>
      <div className="collapse" >
        {!participant?.showScreening && upperFirst(participant?.name)}
        {participant?.showScreening && (
          <Checkbox
            name={`select-screening-form-${participant?.id}`}
            id={`select-screening-form-${participant?.id}`}
            className="collapse-checkbox"
            disabled={!participant?.showScreening}
            label={upperFirst(participant?.name)}
            checked={participant?.isSelected}
            onChange={(checked: boolean) => {
              if (!participant?.showScreening)
                return

              onSelect(checked)
              if (!checked && isOpen)
                handleCollpaseToggle(false)
              if (checked && !isOpen)
                handleCollpaseToggle(true)
            }}
          />
        )}
        <Box className="collapse-action">
          {(!participant?.showScreening || isFilled) && (
            <div className="collapse-badge">Form Filled</div>
          )}
          {participant?.isSelected && (
            <div className="collapse-toggle" onClick={() => handleCollpaseToggle()}>
              {isOpen ? 'Collapse' : 'Expand'}
            </div>
          )}
        </Box>
      </div>
      <div className="content" ref={contentReference}>
        {props.children}
      </div>
    </CollapsableContainer>
  )


  function handleCollpaseToggle(open:boolean = isOpen) {
    if (isOpen && contentReference?.current)
      contentReference.current.style.maxHeight = '0'

    if (!isOpen && contentReference?.current)
      contentReference.current.style.maxHeight = `${contentReference?.current?.scrollHeight}px`

    setOpen(!isOpen)
    onToggle(!isOpen)
  }
}

const CollapsablePreviewContainer =  styled(Box)`
  .collapse-name {
    color: ${({ theme }) => theme.colors.main};
    font-weight: bold;
    font-size: 18px;
    line-height: 24px;
    margin-bottom: 24px;
  }

  &:not(:first-child) {
    &:before {
      content: '';
      width: 100%;
      height: 1px;
      background: #C7C7C7;
      display: block;
      margin-bottom: 40px;
    }
  }
`
const CollapsableContainer = styled(Box)<{$isOpen: boolean}>`
  margin-bottom: 12px !important;
  width: 600px;

  .collapse-checkbox
  div[role="checkbox"].md-text--disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .md-selection-control-label span {
    font-weight: normal;
    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }) => theme?.colors?.secondary};
  }

  .collapse {
    background: ${({ theme }) => lighten(0.60, theme?.colors?.main)};
    width: 100%;
    height: 60px;
    padding: 18px 24px;
    border-radius: 8px;

    ${({$isOpen}) => !$isOpen ? `
      transition: border-radius 0.4s ease-in;
    ` :`
      transition: none;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}

    font-weight: normal;
    font-size: 16px;
    line-height: 24px;
    color: ${({ theme }) => theme?.colors?.secondary};

    display: flex;
    align-items: center;
    justify-content: space-between;

    &-action {
      display: flex;
      align-items: center;
    }

    &-toggle {
      font-weight: bold;
      font-size: 14px;
      line-height: 20px;
      text-decoration-line: underline;
      color: ${({ theme }) => theme?.colors?.main};

      cursor: pointer;
      user-select: none;
      width: 60px;
      margin-left: 24px;
    }

    &-badge {
      font-weight: bold;
      font-size: 14px;
      line-height: 20px;
      color: ${({ theme }) => theme?.colors?.white};

      padding: 4px 16px;
      background: #31B465;
      border-radius: 4px;
    }
  }

  .content {
    padding: 0 24px;
    max-height: 0;
    overflow: hidden;

    ${({$isOpen, theme}) => !$isOpen ? `
      transition: max-height 0.3s ease-out, border 0.5s ease-out !important;
      border: 4px solid transparent;
    ` :`
      transition: max-height 0.3s ease-out !important;
      border: 4px solid ${lighten(0.60, theme?.colors?.main)};
    `}

    & > * {
      width: 100%;
      margin-top: 24px;
      margin-bottom: 24px;
    }
  }
`

export default CollapsableFormContainer
